import { awards as awardDefs, C, conventions, type AwardDef } from "@bd/content";
import { WEEKS_PER_YEAR, yearOf } from "./calendar";
import { news } from "./market";
import type { Company, GameState, MarketGame, Product } from "./state";
import { clamp, log, rng } from "./util";
import { dlog10 } from "./dmath";
import { crowdVotes } from "./cousins";

/**
 * Conventions: Dicefall Con every summer from 1980, then Tabletop Expo and the Harvest Games Fair.
 * Each one hands out its awards to the year's games, yours and the rivals' alike.
 */

export interface AwardResult {
  week: number;
  con: string;
  award: string;
  game: string; // market game id
  name: string;
  publisher: string; // company id or rival id
}

function ownProduct(state: GameState, g: MarketGame): { co: Company; prod: Product } | null {
  const co = state.companies.find((c) => c.id === g.publisher);
  const prod = co?.products.find((p) => p.marketId === g.id);
  return co && prod ? { co, prod } : null;
}

function publisherName(state: GameState, id: string): string {
  return state.companies.find((c) => c.id === id)?.name ?? C.rival[id]?.name ?? id;
}

/** How a game ranks for an award (higher wins). Judges add a little noise; a company's goodwill (events) nudges it. */
function merit(state: GameState, a: AwardDef, g: MarketGame, newcomers: Set<string>, noise: number, votes: Map<string, number>): number | null {
  if (a.audience && !a.audience.includes(g.audience)) return null;
  if (a.format && !a.format.includes(g.format)) return null;
  const own = ownProduct(state, g);
  let m: number;
  switch (a.rule) {
    case "best": m = g.score; break;
    case "newcomer": if (!newcomers.has(g.publisher)) return null; m = g.score; break;
    case "art": m = own ? own.prod.q.art * 10 : g.score * 0.9; break;
    // online, the players are the crowd: their votes decide; the public's favourite only breaks ties
    case "crowd": m = (votes.get(g.id) ?? 0) * 10 + dlog10(1 + g.sold) * 2 + g.score * 0.3; break;
    case "value": m = own ? own.prod.inputs.V * 10 : g.score * 0.85; break;
    case "novel": if (g.copyOf) return null; m = g.score + (state.market.comboCount[`${g.mechanic}|${g.theme}`] === 1 ? 1 : 0); break;
    case "replay": m = own ? own.prod.trueReplay * 10 : g.score * 0.8; break;
    default: return null;
  }
  if (own) m += Math.min(1, own.co.awardBoost);
  return m + noise;
}

export function runConvention(state: GameState, conId: string): AwardResult[] {
  const con = C.convention[conId];
  const r = rng(state, `con:${conId}`);
  const since = state.week - WEEKS_PER_YEAR;
  const pool = state.market.games.filter((g) => g.week > since && g.week <= state.week);
  // a newcomer is a publisher whose first game came out in the last three years
  const firstGame = new Map<string, number>();
  for (const g of state.market.games) firstGame.set(g.publisher, Math.min(firstGame.get(g.publisher) ?? Infinity, g.week));
  const newcomers = new Set([...firstGame].filter(([, w]) => state.week - w < 3 * WEEKS_PER_YEAR).map(([p]) => p));
  const results: AwardResult[] = [];
  const won = new Set<string>();
  const votes = crowdVotes(state);
  for (const id of con.awards) {
    const a = awardDefs.find((x) => x.id === id)!;
    let best: { g: MarketGame; m: number } | null = null;
    for (const g of pool) {
      // spread the minor awards around (but the players' crowd vote stands)
      if (won.has(g.id) && a.rule !== "best" && !(a.rule === "crowd" && votes.size)) continue;
      const m = merit(state, a, g, newcomers, r.normal(0, 0.25), votes);
      if (m !== null && (!best || m > best.m)) best = { g, m };
    }
    if (!best) continue;
    won.add(best.g.id);
    results.push({ week: state.week, con: con.id, award: a.id, game: best.g.id, name: best.g.name, publisher: best.g.publisher });
    const own = ownProduct(state, best.g);
    if (own) {
      const { co, prod } = own;
      co.stats.awards += 1;
      co.reputation = clamp(co.reputation + 3 * con.size, 0, 100);
      co.fans += Math.round(1500 * con.size * (1 + (yearOf(state.week) - 1980) / 25));
      co.mods.push({ kind: "sales", value: 1 + 0.35 * con.size, target: `product:${prod.id}`, until: state.week + 26, label: `${a.name} winner` });
      log(state, "award", `${prod.design.name} wins ${a.name} at ${con.name}! Sales get a lift for six months.`, co.id);
    }
    news(state, "award", `${con.name}: ${a.name} goes to ${best.g.name} (${publisherName(state, best.g.publisher)}).`);
  }
  for (const co of state.companies) co.awardBoost = 0;
  // the card-game world championship at Dicefall Con
  if (con.id === "dicefall") {
    for (const co of state.companies) for (const p of co.products) {
      if (p.meta && p.launched && !p.retired) {
        co.fans += Math.round(600 * p.meta.health);
        log(state, "championship", `The ${p.design.name} world championship fills a hall at ${con.name}${p.meta.dominance > 0.62 ? `, though every finalist plays ${C.archetype[p.meta.top].name}` : ""}.`, co.id);
      }
    }
  }
  state.market.awards = [...(state.market.awards ?? []), ...results].slice(-200);
  return results;
}

export const conventionSystem = {
  name: "conventions",
  week(state: GameState): void {
    const year = yearOf(state.week);
    const woy = state.week % WEEKS_PER_YEAR;
    for (const con of conventions) if (year >= con.year && woy === con.week) runConvention(state, con.id);
  },
};
